import {
    ColoursObj,
    drawerStore,
    Icon,
    MobileDrawerFrame,
    TextInputField,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useSignal, useSignalEffect } from '@preact/signals-react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { sentMessage } from '../../../state/rocketchatState/rocketchatSignals'
import { useThemeContext } from '../../../state/themeState/themeContext'
import { isConnectivityAvailable } from '../../connectivity/connectivityListener'
import { useRocketChatContext } from '../rocketChatProvider'
import { UploadActionSheetBody } from './actionSheet/uploadActionSheetBody'
import { useChatBodyContext } from './chatProvider'
export const SAFE_UPLOAD_TYPES = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/json',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/msword',
    'application/vnd.ms-powerpoint',
]

export const ChatFooter = () => {
    const { api, editMessage } = useChatBodyContext()
    const { rcUserInfo } = useRocketChatContext()
    const { selectedColourScheme } = useThemeContext(
        useShallow((s) => {
            return {
                selectedColourScheme: s.selectedColourScheme,
            }
        })
    )

    const message = useSignal('')
    const editMessageOffset = useSignal(0)
    const inputElement = useRef<HTMLElement>(null)
    const editingMessage = useRef<HTMLElement>(null)
    const [isTyping, setIsTyping] = useState(false)
    const [inputBoxFocus, setInputBoxFocus] = useState(false)

    useSignalEffect(() => {
        if (editMessage?.value) {
            message.value = editMessage.value.msg
        }
    })

    useSignalEffect(() => {
        if (inputElement.current && editingMessage.current) {
            editMessageOffset.value =
                inputElement.current.clientHeight +
                editingMessage.current.clientHeight
        } else {
            editMessageOffset.value = 0
        }
    })

    // this is to limit the amount of typing status messages sent
    useEffect(() => {
        let typingTimeout: NodeJS.Timeout
        if (isTyping && rcUserInfo && api?.value?.chatApi) {
            if (!rcUserInfo.username) return
            api.value.sendTypingStatus(rcUserInfo.username, true)

            typingTimeout = setTimeout(() => {
                setIsTyping(false)
                if (!rcUserInfo.username) return
                api?.value?.sendTypingStatus(rcUserInfo.username, false)
            }, 10000)
        }

        return () => {
            clearTimeout(typingTimeout)
        }
    }, [isTyping])
    const actionColour = useMemo(() => {
        if (selectedColourScheme === 'custom') {
            return ColoursObj.core.secondary
        }
        return ColoursObj.shades.shade4
    }, [selectedColourScheme])

    const onMessageChange = (e: any) => {
        // we only want to send typing status once per 20 seconds
        setIsTyping(true)
        message.value = e.target.value
    }
    const handleKeyDown = (event: any) => {
        if (event.key === 'Enter') {
        }
    }

    const sendMessage = () => {
        if (api?.value?.chatApi) {
            sentMessage.value = true
            if (editMessage?.value) {
                api.value.updateMessage(editMessage.value._id, message.value)
                editMessage.value = null
                message.value = ''
                return
            }
            api.value.sendMessage(message.value, null)
            message.value = ''
        } else {
            defaultAddSnack('Cannot send message, try again later', 'warning')
        }
    }

    const rightAction = () => {
        return (
            <AnimatePresence>
                <Box position={'relative'}>
                    {message.value.length > 0 && (
                        <motion.div
                            layoutId="rightAction"
                            key={'send'}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{
                                duration: 0.2,
                            }}
                            style={{ overflow: 'clip' }}
                        >
                            <Icon onClick={() => sendMessage()} name="Send" />
                        </motion.div>
                    )}
                    {message.value.length === 0 && (
                        <motion.div
                            layoutId="rightAction"
                            key={'document'}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0 }}
                            transition={{
                                duration: 0.2,
                            }}
                            style={{ overflow: 'clip' }}
                        >
                            <Icon
                                onClick={() => {
                                    if (
                                        isConnectivityAvailable.value &&
                                        api?.value?.chatApi
                                    ) {
                                        drawerStore().openDrawer({
                                            node: (
                                                <MobileDrawerFrame
                                                    title="Add"
                                                    minHeight="7vh"
                                                >
                                                    <UploadActionSheetBody />
                                                </MobileDrawerFrame>
                                            ),
                                            onBackdropClick: () => {
                                                drawerStore().closeDrawer()
                                            },
                                        })
                                    }
                                }}
                                name="Add"
                            />
                        </motion.div>
                    )}
                </Box>
            </AnimatePresence>
        )
    }

    const leftAction = () => {
        return (
            <AnimatePresence>
                {editMessage?.value && (
                    <motion.div
                        layoutId="leftAction"
                        key={'cancel'}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{
                            duration: 0.2,
                        }}
                        style={{ overflow: 'clip' }}
                    >
                        <Icon
                            onClick={() => {
                                editMessage.value = null
                                message.value = ''
                            }}
                            name="Cancel"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        )
    }

    return (
        <div>
            {editMessage?.value && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingBottom: '8px',
                    }}
                >
                    <Box
                        ref={editingMessage}
                        maxWidth={'250px'}
                        fontSize={'24px'}
                        maxHeight={'10em'}
                        bgcolor={ColoursObj.backgrounds.secondary}
                        borderRadius={'8px'}
                        padding={'8px'}
                        display={'flex'}
                        flexWrap={'wrap'}
                        alignContent={'stretch'}
                        justifyContent={'flex-end'}
                        alignItems={'flex-end'}
                        overflow="scroll"
                    >
                        <Typography
                            variant="bodyRegular"
                            width={'fit-content'}
                            style={{ wordBreak: 'break-word' }}
                        >
                            {editMessage.value.msg}
                        </Typography>
                    </Box>
                </div>
            )}
            <>
                <Box
                    padding={'4px 16px 0px 16px'}
                    ref={inputElement}
                    bgcolor={ColoursObj.backgrounds.primary}
                >
                    <Stack
                        direction="row"
                        spacing={'8px'}
                        alignItems={'flex-end'}
                    >
                        <TextInputField
                            disabled={
                                isConnectivityAvailable.value ? false : true
                            }
                            muiTextFieldProps={{
                                onKeyDown: (e) => {
                                    handleKeyDown(e)
                                },
                                multiline: true,
                            }}
                            inputStyle={{
                                borderRadius: '50px',
                                border: `1px solid ${ColoursObj.shades.shade2}`,
                                marginTop: '0px',

                                backgroundColor: ColoursObj.backgrounds.input,
                                minHeight: '40px',
                                padding: '0px 8px',
                                height: 'unset',
                            }}
                            value={message.value}
                            style={{
                                marginTop: '0px',
                                width: 'calc(100% - 48px)',
                            }}
                            onChange={onMessageChange}
                            onFocus={() => {
                                setInputBoxFocus(true)
                                api?.value?.chatApi &&
                                    rcUserInfo &&
                                    api.value.sendTypingStatus(
                                        rcUserInfo.name,
                                        true
                                    )
                            }}
                            onBlur={() => {
                                setInputBoxFocus(false)
                                api?.value?.chatApi &&
                                    rcUserInfo &&
                                    api.value.sendTypingStatus(
                                        rcUserInfo.name,
                                        false
                                    )
                            }}
                            helperText={i18n.t('rocketChat.messagePlaceHolder')}
                            iconLeft={leftAction()}
                        />
                        <Box
                            borderRadius={'100px'}
                            bgcolor={actionColour}
                            width={'40px!important'}
                            height={'40px'}
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                        >
                            {rightAction()}
                        </Box>
                    </Stack>
                    {/* need make multi line */}
                </Box>
                {/* Bottom Filler */}
                <Box height={8} />
            </>
        </div>
    )
}
